import Phaser from "phaser";

import { COLORS, FONT } from "./theme.ts";

export type RingPlayer = {
  userId: string;
  displayName: string;
  avatarUrl: string;
  hasVoted: boolean;
  connected: boolean;
  score: number;
};

export type AvatarRingOptions = {
  onSelect?: ((userId: string) => void) | undefined;
};

type Bubble = {
  container: Phaser.GameObjects.Container;
  ring: Phaser.GameObjects.Arc;
  image: Phaser.GameObjects.Image | null;
  fallback: Phaser.GameObjects.Text;
  name: Phaser.GameObjects.Text;
  badge: Phaser.GameObjects.Text;
  tally: Phaser.GameObjects.Text;
};

const ROUND_TEXTURE_SIZE = 128;

/**
 * Copies a loaded avatar into a circular canvas texture once, so bubbles are
 * plain Images with no geometry masks (which do not follow nested containers).
 */
function makeRoundTexture(scene: Phaser.Scene, sourceKey: string, targetKey: string): void {
  const source = scene.textures.get(sourceKey).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  const canvas = scene.textures.createCanvas(targetKey, ROUND_TEXTURE_SIZE, ROUND_TEXTURE_SIZE);
  if (!canvas) return;
  const ctx = canvas.context;
  const half = ROUND_TEXTURE_SIZE / 2;
  ctx.save();
  ctx.beginPath();
  ctx.arc(half, half, half, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(source, 0, 0, ROUND_TEXTURE_SIZE, ROUND_TEXTURE_SIZE);
  ctx.restore();
  canvas.refresh();
}

/**
 * The players arranged in a circle. It is the one piece of UI that persists
 * through every phase: voting taps it, reveal counts on it, scoreboard ranks
 * it. Owns avatar loading so scenes only hand it data.
 */
export class AvatarRing extends Phaser.GameObjects.Container {
  private readonly _bubbles = new Map<string, Bubble>();
  private readonly _onSelect: ((userId: string) => void) | undefined;
  private _selectedUserId: string | null = null;
  private _selectable = false;
  private _showVoteBadges = false;
  private _radius = 140;
  private _bubbleRadius = 36;
  private _myUserId = "";
  private _order: string[] = [];

  constructor(scene: Phaser.Scene, x: number, y: number, options: AvatarRingOptions = {}) {
    super(scene, x, y);
    this._onSelect = options.onSelect;
    scene.add.existing(this);
  }

  setMyUserId(userId: string): void {
    this._myUserId = userId;
  }

  /** Idempotent: diffs against the current bubbles, never rebuilds everything. */
  setPlayers(players: RingPlayer[]): void {
    const incoming = new Set(players.map((p) => p.userId));
    for (const [userId, bubble] of this._bubbles) {
      if (!incoming.has(userId)) {
        bubble.container.destroy();
        this._bubbles.delete(userId);
      }
    }
    for (const player of players) {
      const bubble = this._bubbles.get(player.userId) ?? this._createBubble(player);
      this._bubbles.set(player.userId, bubble);
      this._applyPlayer(bubble, player);
    }
    this._order = players.map((p) => p.userId);
    this.layout();
  }

  /** Allow tapping other players. Selection state is local until the server confirms. */
  setSelectable(selectable: boolean): void {
    this._selectable = selectable;
    if (!selectable) this._selectedUserId = null;
    for (const [userId, bubble] of this._bubbles) {
      const canTap = selectable && userId !== this._myUserId;
      if (canTap) bubble.container.setInteractive({ useHandCursor: true });
      else bubble.container.disableInteractive();
      this._refreshRing(userId, bubble);
    }
  }

  /** "locked in" badges only mean something while voting is open. */
  setShowVoteBadges(show: boolean): void {
    this._showVoteBadges = show;
    for (const bubble of this._bubbles.values()) {
      if (!show) bubble.badge.setVisible(false);
    }
  }

  setSelected(userId: string | null): void {
    this._selectedUserId = userId;
    for (const [id, bubble] of this._bubbles) this._refreshRing(id, bubble);
  }

  /** Reveal: show vote counts on each bubble; pass null to clear. */
  setTally(tally: Record<string, number> | null, winnerIds: readonly string[] = []): void {
    for (const [userId, bubble] of this._bubbles) {
      const count = tally?.[userId];
      const isWinner = winnerIds.includes(userId);
      bubble.tally.setText(count === undefined ? "" : String(count));
      bubble.tally.setVisible(count !== undefined);
      bubble.ring.setStrokeStyle(isWinner ? 6 : 4, isWinner ? COLORS.accent : this._ringColor(userId));
      if (isWinner) {
        this.scene.tweens.add({ targets: bubble.container, scale: 1.15, yoyo: true, duration: 220, repeat: 2 });
      }
    }
  }

  /** Screen-space centre of a bubble, for end-to-end tests that tap the canvas. */
  bubbleScreenPosition(userId: string): { x: number; y: number } | null {
    const bubble = this._bubbles.get(userId);
    if (!bubble) return null;
    const m = bubble.container.getWorldTransformMatrix();
    return { x: m.tx, y: m.ty };
  }

  resize(radius: number, bubbleRadius: number): void {
    this._radius = radius;
    this._bubbleRadius = bubbleRadius;
    for (const bubble of this._bubbles.values()) this._sizeBubble(bubble);
    this.layout();
  }

  layout(): void {
    const count = this._order.length;
    this._order.forEach((userId, index) => {
      const bubble = this._bubbles.get(userId);
      if (!bubble) return;
      const angle = -Math.PI / 2 + (index / Math.max(1, count)) * Math.PI * 2;
      bubble.container.setPosition(Math.cos(angle) * this._radius, Math.sin(angle) * this._radius);
    });
  }

  private _createBubble(player: RingPlayer): Bubble {
    const scene = this.scene;
    const container = scene.add.container(0, 0);
    const ring = scene.add.circle(0, 0, this._bubbleRadius, COLORS.surface).setStrokeStyle(4, COLORS.ringIdle);
    const fallback = scene.add
      .text(0, 0, player.displayName.slice(0, 1).toUpperCase(), {
        fontFamily: FONT.family,
        fontSize: `${FONT.prompt}px`,
        color: COLORS.text,
      })
      .setOrigin(0.5);
    const name = scene.add
      .text(0, this._bubbleRadius + 6, player.displayName, {
        fontFamily: FONT.family,
        fontSize: `${FONT.small}px`,
        color: COLORS.text,
      })
      .setOrigin(0.5, 0);
    const badge = scene.add
      .text(this._bubbleRadius * 0.7, -this._bubbleRadius * 0.7, "", {
        fontFamily: FONT.family,
        fontSize: `${FONT.small}px`,
        color: "#1b1530",
        backgroundColor: "#5ee0a0",
        padding: { x: 5, y: 2 },
      })
      .setOrigin(0.5)
      .setVisible(false);
    const tally = scene.add
      .text(0, -this._bubbleRadius - 4, "", {
        fontFamily: FONT.family,
        fontSize: `${FONT.body}px`,
        color: COLORS.accentText,
        fontStyle: "bold",
      })
      .setOrigin(0.5, 1)
      .setVisible(false);

    container.add([ring, fallback, name, badge, tally]);
    container.setSize(this._bubbleRadius * 2, this._bubbleRadius * 2);
    container.on("pointerdown", () => {
      if (!this._selectable || player.userId === this._myUserId) return;
      this.setSelected(player.userId);
      this._onSelect?.(player.userId);
    });
    this.add(container);

    const bubble: Bubble = { container, ring, image: null, fallback, name, badge, tally };
    this._loadAvatar(bubble, player);
    return bubble;
  }

  private _loadAvatar(bubble: Bubble, player: RingPlayer): void {
    if (!player.avatarUrl) return;
    const rawKey = `avatar-raw:${player.userId}`;
    const roundKey = `avatar:${player.userId}`;
    const attach = () => {
      if (!this._bubbles.has(player.userId) || bubble.image) return;
      if (!this.scene.textures.exists(roundKey)) makeRoundTexture(this.scene, rawKey, roundKey);
      const image = this.scene.add.image(0, 0, roundKey);
      bubble.image = image;
      bubble.fallback.setVisible(false);
      bubble.container.addAt(image, 1);
      this._sizeBubble(bubble);
    };
    if (this.scene.textures.exists(rawKey)) {
      attach();
      return;
    }
    this.scene.load.image(rawKey, player.avatarUrl);
    this.scene.load.once(`filecomplete-image-${rawKey}`, attach);
    this.scene.load.start();
  }

  private _sizeBubble(bubble: Bubble): void {
    const r = this._bubbleRadius;
    bubble.ring.setRadius(r);
    bubble.container.setSize(r * 2, r * 2);
    bubble.name.setY(r + 6);
    bubble.badge.setPosition(r * 0.7, -r * 0.7);
    bubble.tally.setY(-r - 4);
    bubble.image?.setDisplaySize(r * 2 - 8, r * 2 - 8);
  }

  private _applyPlayer(bubble: Bubble, player: RingPlayer): void {
    bubble.name.setText(player.displayName);
    bubble.container.setAlpha(player.connected ? 1 : 0.45);
    bubble.badge.setText("locked in").setVisible(this._showVoteBadges && player.hasVoted);
    this._refreshRing(player.userId, bubble);
  }

  private _ringColor(userId: string): number {
    if (userId === this._selectedUserId) return COLORS.ringSelected;
    if (userId === this._myUserId) return COLORS.surfaceRaised;
    return COLORS.ringIdle;
  }

  private _refreshRing(userId: string, bubble: Bubble): void {
    bubble.ring.setStrokeStyle(userId === this._selectedUserId ? 6 : 4, this._ringColor(userId));
  }
}
